// src/router.tsx
import { lazy, Suspense } from 'react';
import { createBrowserRouter, Navigate } from 'react-router-dom';
import LoginPage from '@/pages/auth/LoginPage';

import SignupWithPlansPage from '@/pages/auth/SignupWithPlansPage';
import PasswordResetPage from '@/pages/auth/PasswordResetPage';
import AppPasswordResetGate from '@/pages/auth/AppPasswordResetGate';
import UpdatePasswordPage from '@/pages/auth/UpdatePasswordPage';
import CallbackPage from '@/pages/auth/CallbackPage';
import ProtectedRoute from '@/components/auth/ProtectedRoute';
import ProtectedPartnerRoute from '@/components/auth/ProtectedPartnerRoute';
import AdminRoute from '@/components/auth/AdminRoute';
import DashboardLayout from '@/components/layouts/DashboardLayout';
import PartnerLayout from '@/components/layouts/PartnerLayout';
import AdminLayout from '@/components/layouts/AdminLayout';
// BookingPage lazy-loaded below with other pages

// PageLoader shown while lazy chunks load
function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[50vh]">
      <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
    </div>
  );
}

// Lazy-loaded Dashboard pages
const DashboardPage = lazy(() => import('@/pages/dashboard/DashboardPage'));
const CallsPage = lazy(() => import('@/pages/dashboard/CallsPage'));
const CallQueuePage = lazy(() => import('@/pages/dashboard/CallQueuePage'));
const IntegrationsPage = lazy(() => import('@/pages/dashboard/IntegrationsPage'));
const AccountPage = lazy(() => import('@/pages/dashboard/AccountPage'));
const ResourcesPage = lazy(() => import('@/pages/dashboard/ResourcesPage'));
const HeroCommands = lazy(() => import('@/pages/dashboard/HeroCommandsPage'));
const BillingPage = lazy(() => import('@/pages/dashboard/BillingPage'));
const EmailsPage = lazy(() => import('@/pages/dashboard/EmailsPage'));
const CRMDashboardPage = lazy(() => import('@/pages/dashboard/crm/CRMDashboardPage'));
const ContactsPage = lazy(() => import('@/pages/dashboard/crm/ContactsPage'));
const ContactDetailPage = lazy(() => import('@/pages/dashboard/crm/ContactDetailPage'));
const DealsPage = lazy(() => import('@/pages/dashboard/crm/DealsPage'));
const AICommunicationsPage = lazy(() => import('@/pages/dashboard/crm/AICommunicationsPage'));
const ActivitiesPage = lazy(() => import('@/pages/dashboard/crm/ActivitiesPage'));
const CommunicationsPage = lazy(() => import('@/pages/dashboard/crm/CommunicationsPage'));
const AnalyticsPage = lazy(() => import('@/pages/dashboard/crm/AnalyticsPage'));
const InboxPage = lazy(() => import('@/pages/dashboard/crm/InboxPage'));
const JadeSmsPage = lazy(() => import('@/pages/dashboard/JadeSmsPage'));
const BookingPagesPage = lazy(() => import('@/pages/dashboard/BookingPagesPage'));
const BookingPageEditorPage = lazy(() => import('@/pages/dashboard/BookingPageEditorPage'));
const LanguageSettingsPage = lazy(() => import('@/pages/dashboard/LanguageSettingsPage'));
const AIEmployeesPage = lazy(() => import('@/pages/dashboard/AIEmployeesPage'));
const AIEmployeeSettingsPage = lazy(() => import('@/pages/dashboard/AIEmployeeSettingsPage'));
const ROIPage = lazy(() => import('@/pages/dashboard/ROIPage'));
const WorkflowsPage = lazy(() => import('@/pages/dashboard/WorkflowsPage'));
const CallSummariesPage = lazy(() => import('@/pages/dashboard/CallSummariesPage'));
const FollowUpsPage = lazy(() => import('@/pages/dashboard/FollowUpsPage'));
const ReviewsPage = lazy(() => import('@/pages/dashboard/ReviewsPage'));
const BusinessOverviewPage = lazy(() => import('@/pages/dashboard/BusinessOverviewPage'));
const NewContactPage = lazy(() => import('@/pages/dashboard/crm/NewContactPage'));
const WhatsNewPage = lazy(() => import('@/pages/dashboard/WhatsNewPage'));

// Lazy-loaded Onboarding page
const OnboardingPage = lazy(() => import('@/pages/onboarding/OnboardingPage'));

// Public marketing pages (replace the GoHighLevel www site). The homepage
// redirects authenticated users to the dashboard, preserving the old "/"
// behavior.
const HomePage = lazy(() => import('@/pages/marketing/HomePage'));
const IndustriesPage = lazy(() => import('@/pages/marketing/IndustriesPage'));
const DemoCallConfirmationPage = lazy(
  () => import('@/pages/marketing/DemoCallConfirmationPage')
);

// Public demo-AI consent landing (Twilio A2P CTA target)
const DemoAiPage = lazy(() => import('@/pages/demo/DemoAiPage'));

// Public, repo-managed legal pages
const PrivacyPolicyPage = lazy(() => import('@/pages/legal/PrivacyPolicyPage'));
const TermsOfServicePage = lazy(() => import('@/pages/legal/TermsOfServicePage'));

// Lazy-loaded Partner pages
const PartnerDashboard = lazy(() => import('@/pages/partner/PartnerDashboard'));
const StripeCallback = lazy(() => import('@/pages/partner/StripeCallback'));

// Lazy-loaded Billing pages
const BillingSuccessPage = lazy(() => import('@/pages/billing/BillingSuccessPage'));
const BillingCancelPage = lazy(() => import('@/pages/billing/BillingCancelPage'));

// Lazy-loaded public pages
const BookingPage = lazy(() => import('@/pages/BookingPage'));
const PreviewPage = lazy(() => import('@/pages/PreviewPage'));

// Lazy-loaded Admin pages
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const TutorialsManager = lazy(() => import('@/pages/admin/TutorialsManager'));
const UserManagement = lazy(() => import('@/pages/admin/UserManagement'));
const A2pQueuePage = lazy(() => import('@/pages/admin/A2pQueuePage'));
const AdminPlanManagementPage = lazy(() => import('@/pages/admin/AdminPlanManagementPage'));
const PartnersManagementPage = lazy(() => import('@/pages/admin/PartnersManagementPage'));
const AdminDormantAccountsPage = lazy(() => import('@/pages/admin/AdminDormantAccountsPage'));
const AdminOpsHubPage = lazy(() => import('@/pages/admin/AdminOpsHubPage'));
const AdminPromptsPage = lazy(() => import('@/pages/admin/AdminPromptsPage'));
const AdminTrialsPage = lazy(() => import('@/pages/admin/AdminTrialsPage'));
const AdminSubscribersPage = lazy(() => import('@/pages/admin/AdminSubscribersPage'));
const AdminPnlPage = lazy(() => import('@/pages/admin/AdminPnlPage'));
const AdminTransactionsPage = lazy(() => import('@/pages/admin/AdminTransactionsPage'));
const AdminAudiencePage = lazy(() => import('@/pages/admin/AdminAudiencePage'));
const AdminWhopPage = lazy(() => import('@/pages/admin/AdminWhopPage'));
const AdminAddonsPage = lazy(() => import('@/pages/admin/AdminAddonsPage'));
const AdminExpensesPage = lazy(() => import('@/pages/admin/AdminExpensesPage'));

// Helper to wrap an element in Suspense with PageLoader fallback
function withSuspense(element: React.ReactNode) {
  return <Suspense fallback={<PageLoader />}>{element}</Suspense>;
}

export const router = createBrowserRouter([
  // Public marketing pages — the homepage redirects authenticated users to
  // the dashboard inside HomePage itself.
  { path: '/', element: withSuspense(<HomePage />) },
  { path: '/industries', element: withSuspense(<IndustriesPage />) },
  { path: '/demo-call-confirmation', element: withSuspense(<DemoCallConfirmationPage />) },
  // Old GoHighLevel marketing URLs (search-indexed pre-cutover) — keep
  // that traffic on marketing pages instead of the catch-all login.
  { path: '/home', element: <Navigate to="/" replace /> },
  { path: '/industries-we-serve', element: <Navigate to="/industries" replace /> },
  { path: '/pricing', element: <Navigate to="/" replace /> },
  { path: '/whitelabel-waitlist', element: <Navigate to="/" replace /> },
  {
    element: <ProtectedRoute />,
    children: [
      {
        path: 'onboarding',
        element: withSuspense(<OnboardingPage />),
      },
      {
        path: 'dashboard',
        element: <DashboardLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/dashboard/dashboard" replace />,
          },
          { path: 'dashboard', element: withSuspense(<DashboardPage />) },
          { path: 'call-queue', element: withSuspense(<CallQueuePage />) },
          { path: 'calls', element: withSuspense(<CallsPage />) },
          { path: 'emails', element: withSuspense(<EmailsPage />) },
          { path: 'sms', element: withSuspense(<JadeSmsPage />) },
          { path: 'booking-pages', element: withSuspense(<BookingPagesPage />) },
          { path: 'booking-pages/editor', element: withSuspense(<BookingPageEditorPage />) },
          { path: 'booking-pages/editor/:id', element: withSuspense(<BookingPageEditorPage />) },
          { path: 'crm', element: withSuspense(<CRMDashboardPage />) },
          { path: 'crm/contacts', element: withSuspense(<ContactsPage />) },
          { path: 'crm/contacts/new', element: withSuspense(<NewContactPage />) },
          { path: 'crm/contacts/:id', element: withSuspense(<ContactDetailPage />) },
          { path: 'crm/deals', element: withSuspense(<DealsPage />) },
          { path: 'crm/inbox', element: withSuspense(<InboxPage />) },
          { path: 'crm/ai-communications', element: withSuspense(<AICommunicationsPage />) },
          { path: 'crm/activities', element: withSuspense(<ActivitiesPage />) },
          { path: 'crm/communications', element: withSuspense(<CommunicationsPage />) },
          { path: 'crm/analytics', element: withSuspense(<AnalyticsPage />) },
          { path: 'receptionist-settings', element: <Navigate to="/dashboard/ai-employees" replace /> },
          { path: 'integrations', element: withSuspense(<IntegrationsPage />) },
          { path: 'hero-commands', element: withSuspense(<HeroCommands />) },
          { path: 'language-settings', element: withSuspense(<LanguageSettingsPage />) },
          { path: 'ai-employees', element: withSuspense(<AIEmployeesPage />) },
          { path: 'ai-employees/:id', element: withSuspense(<AIEmployeeSettingsPage />) },
          { path: 'business-overview', element: withSuspense(<BusinessOverviewPage />) },
          { path: 'roi', element: withSuspense(<ROIPage />) },
          { path: 'workflows', element: withSuspense(<WorkflowsPage />) },
          { path: 'call-summaries', element: withSuspense(<CallSummariesPage />) },
          { path: 'follow-ups', element: withSuspense(<FollowUpsPage />) },
          { path: 'reviews', element: withSuspense(<ReviewsPage />) },
          { path: 'account', element: withSuspense(<AccountPage />) },
          { path: 'billing', element: withSuspense(<BillingPage />) },
          { path: 'resources', element: withSuspense(<ResourcesPage />) },
          { path: 'whats-new', element: withSuspense(<WhatsNewPage />) },
          { path: 'partner', element: <Navigate to="/partner/dashboard" replace /> },
        ],
      },
      {
        path: 'partner',
        element: <PartnerLayout />,
        children: [
          {
            index: true,
            element: <Navigate to="/partner/dashboard" replace />,
          },
          {
            path: 'dashboard',
            element: withSuspense(<PartnerDashboard />),
          },
          {
            path: 'sub-accounts',
            element: withSuspense(<PartnerDashboard />),
          },
          {
            path: 'payouts',
            element: withSuspense(<PartnerDashboard />),
          },
          {
            path: 'commissions',
            element: withSuspense(<PartnerDashboard />),
          },
          {
            path: 'billing',
            element: (
              <ProtectedPartnerRoute requireWhiteLabel={true}>
                {withSuspense(<PartnerDashboard />)}
              </ProtectedPartnerRoute>
            ),
          },
          {
            path: 'tutorials',
            element: withSuspense(<PartnerDashboard />),
          },
          {
            path: 'domains',
            element: (
              <ProtectedPartnerRoute requireWhiteLabel={true}>
                {withSuspense(<PartnerDashboard />)}
              </ProtectedPartnerRoute>
            ),
          },
          {
            path: 'branding',
            element: (
              <ProtectedPartnerRoute requireWhiteLabel={true}>
                {withSuspense(<PartnerDashboard />)}
              </ProtectedPartnerRoute>
            ),
          },
          {
            path: 'pricing',
            element: withSuspense(<PartnerDashboard />),
          },
          {
            path: 'help-center',
            element: (
              <ProtectedPartnerRoute requireWhiteLabel={true}>
                {withSuspense(<PartnerDashboard />)}
              </ProtectedPartnerRoute>
            ),
          },
          {
            path: 'settings',
            element: withSuspense(<PartnerDashboard />),
          },
        ],
      },
    ],
  },
  { path: '/login', element: <LoginPage /> },
  // Direct signup (also the target of LoginPage's "Sign up" link, which
  // previously fell through to the catch-all).
  { path: '/signup', element: <SignupWithPlansPage /> },
  // Affiliate program retired (Rewardful removed 2026-07); old links in the
  // wild still land on a working signup instead of a login dead-end.
  { path: '/affiliate/signup', element: <Navigate to="/signup" replace /> },
  { path: '/password-reset', element: <PasswordResetPage /> },
  { path: '/password-reset/gate', element: <AppPasswordResetGate /> },
  { path: '/update-password', element: <UpdatePasswordPage /> },
  { path: '/auth/callback', element: <CallbackPage /> },
  { path: '/partner/stripe-callback', element: withSuspense(<StripeCallback />) },
  { path: '/preview', element: withSuspense(<PreviewPage />) },
  { path: '/book/:slug', element: withSuspense(<BookingPage />) },
  { path: '/orucubg', element: withSuspense(<DemoAiPage />) },
  { path: '/privacy-policy', element: withSuspense(<PrivacyPolicyPage />) },
  { path: '/terms-of-service', element: withSuspense(<TermsOfServicePage />) },

  { path: '/billing/success', element: withSuspense(<BillingSuccessPage />) },
  { path: '/billing/cancel', element: withSuspense(<BillingCancelPage />) },

  // Admin routes - protected by AdminRoute with AdminLayout
  {
    path: '/admin',
    element: <AdminRoute />,
    children: [
      {
        element: <AdminLayout />,
        children: [
          { index: true, element: <Navigate to="/admin/dashboard" replace /> },
          { path: 'dashboard', element: withSuspense(<AdminDashboard />) },
          { path: 'pnl', element: withSuspense(<AdminPnlPage />) },
          { path: 'trials', element: withSuspense(<AdminTrialsPage />) },
          { path: 'subscribers', element: withSuspense(<AdminSubscribersPage />) },
          { path: 'transactions', element: withSuspense(<AdminTransactionsPage />) },
          { path: 'addons', element: withSuspense(<AdminAddonsPage />) },
          { path: 'expenses', element: withSuspense(<AdminExpensesPage />) },
          { path: 'audience', element: withSuspense(<AdminAudiencePage />) },
          { path: 'whop', element: withSuspense(<AdminWhopPage />) },
          { path: 'users', element: withSuspense(<UserManagement />) },
          { path: 'partners', element: withSuspense(<PartnersManagementPage />) },
          { path: 'a2p-queue', element: withSuspense(<A2pQueuePage />) },
          { path: 'plans', element: withSuspense(<AdminPlanManagementPage />) },
          { path: 'prompts', element: withSuspense(<AdminPromptsPage />) },
          { path: 'dormant', element: withSuspense(<AdminDormantAccountsPage />) },
          { path: 'ops', element: withSuspense(<AdminOpsHubPage />) },
          { path: 'tutorials', element: withSuspense(<TutorialsManager />) },
        ],
      },
    ],
  },

  {
      path: '*',
      element: <Navigate to="/login" replace />,
  }
]);
